import React, { useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { usePortals } from 'react-portal-hook';
import { PostcodeModal } from '~/common/components';
import { GetUserLocationState, UserLocation } from '~/common/store';
import { OfferListing } from '~/common/models';
import { ActiveSpecialOffers, SelectedOfferCategory } from '../../store';
import styles from './index.module.scss';
import { Dropdown } from '~/common/components/ui-elements';

type OfferHeaderProps = {
  onLoaded?: () => void;
};

export const OfferHeader = ({ onLoaded }: OfferHeaderProps) => {
  useEffect(() => {
    onLoaded?.();
  }, [onLoaded]);

  return (
    <div className={styles.offerHeader}>
      <OffersLocation />
    </div>
  );
};

export const OffersLocation = React.memo(() => {
  const portalManager = usePortals();
  const locationState = useRecoilValue(GetUserLocationState);
  const setActiveOffers = useSetRecoilState(ActiveSpecialOffers);

  const handleLocationChange = ({ postcode }: UserLocation) => {
    fetch(`/api/v2/offers/${postcode}`).then(async (res) => {
      const offers = (await res.json()) as OfferListing[];
      setActiveOffers(offers);
    });
  };

  const selectLocation = () => {
    portalManager.open((portal) => (
      <PostcodeModal
        onLocationSelect={handleLocationChange}
        closeModal={portal.close}
        buttonText="View Offers"
      />
    ));
  };

  return (
    <>
      {!!locationState ? (
        <div className={styles.offersHeader__hasLocation}>
          <div>YOUR LOCATION:</div>
          <div>
            <div className={styles.offersHeader__location}>
              {locationState.fullName?.replace('}', '')}
            </div>
            <a
              href="#"
              className={styles.offersHeader__locationEdit}
              onClick={(e) => {
                e.preventDefault();
                selectLocation();
              }}
            >
              edit
            </a>
          </div>
        </div>
      ) : (
        <div className={styles.offersHeader__noLocation}>
          <div>See offers in your area:</div>
          <a
            href="#"
            className={styles.offersHeader__locationSet}
            onClick={(e) => {
              e.preventDefault();
              selectLocation();
            }}
          >
            Set location
          </a>
        </div>
      )}
    </>
  );
});

const OffersCategory = React.memo(() => {
  const offers = useRecoilValue(ActiveSpecialOffers);
  const selectedCategory = useRecoilValue(SelectedOfferCategory);
  const setSelectedCategory = useSetRecoilState(SelectedOfferCategory);

  function getDistinctCategories(data: OfferListing[]) {
    const categories = new Set<string>();
    data.forEach((item) => {
      categories.add(item.category ?? '');
    });
    return Array.from(categories);
  }

  const categories = React.useMemo(() => {
    const options = [
      {
        label: 'All Categories',
        value: 'All Categories',
      },
      ...getDistinctCategories(offers).map((category) => ({
        label: category,
        value: category,
      })),
    ];

    return options;
  }, [offers]);

  return (
    <div className={styles.offerCategories}>
      <div>CATEGORIES</div>
      <Dropdown
        width={200}
        defaultValue={selectedCategory}
        onChange={(o) =>
          setSelectedCategory({
            label: o?.label ?? 'All Categories',
            value: o?.value ?? 'All Categories',
          })
        }
        options={categories}
        className={styles.categoryDropdown}
      />
    </div>
  );
});
